import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import useFilterStore from '../store/filterStore';

/**
 * Job Filters Modal Component
 * Contains all 10 filter sections matching Upwork's filter modal
 */
const JobFiltersModal = ({ onClose, onApply }) => {
  const {
    filters,
    setFilter,
    toggleFilterValue,
    clearAllFilters,
    activeFilterCount
  } = useFilterStore();

  const [expandedSections, setExpandedSections] = useState({
    datePosted: true,
    category: true,
    experienceLevel: true,
    jobType: true,
    proposalRange: false,
    clientInfo: false,
    clientHistory: false,
    clientLocation: false,
    clientTimeZones: false,
    projectLength: false,
    budgetRange: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleApply = () => {
    if (onApply) {
      onApply(filters);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Date Posted Filter */}
          <FilterSection
            title="Posted Within"
            isExpanded={expandedSections.datePosted}
            onToggle={() => toggleSection('datePosted')}
          >
            <div className="space-y-2">
              {[
                { value: '24h', label: 'Last 24 hours' },
                { value: '3d', label: 'Last 3 days' },
                { value: '7d', label: 'Last week' },
                { value: '14d', label: 'Last 2 weeks' },
                { value: '30d', label: 'Last month' }
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="datePosted"
                    checked={filters.datePosted === option.value}
                    onChange={() => setFilter('datePosted', filters.datePosted === option.value ? '' : option.value)}
                    className="rounded-full border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
              {filters.datePosted && (
                <button
                  type="button"
                  onClick={() => setFilter('datePosted', '')}
                  className="text-xs text-primary-600 hover:text-primary-700 mt-1"
                >
                  Clear
                </button>
              )}
            </div>
          </FilterSection>

          {/* Category Filter */}
          <FilterSection
            title="Category"
            isExpanded={expandedSections.category}
            onToggle={() => toggleSection('category')}
          >
            <select
              value={filters.category}
              onChange={(e) => setFilter('category', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            >
              <option value="">All Categories</option>
              <option value="web-development">Web Development</option>
              <option value="mobile-development">Mobile Development</option>
              <option value="design">Design & Creative</option>
              <option value="writing">Writing & Translation</option>
              <option value="admin">Admin Support</option>
              <option value="customer-service">Customer Service</option>
              <option value="marketing">Sales & Marketing</option>
              <option value="accounting">Accounting & Consulting</option>
            </select>
          </FilterSection>

          {/* Experience Level Filter */}
          <FilterSection
            title="Experience Level"
            isExpanded={expandedSections.experienceLevel}
            onToggle={() => toggleSection('experienceLevel')}
          >
            <div className="space-y-2">
              {[
                { value: 'entry', label: 'Entry Level', desc: 'Looking for simple tasks' },
                { value: 'intermediate', label: 'Intermediate', desc: 'Looking for moderate complexity' },
                { value: 'expert', label: 'Expert', desc: 'Looking for comprehensive expertise' }
              ].map((option) => (
                <label key={option.value} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.experienceLevels.includes(option.value)}
                    onChange={() => toggleFilterValue('experienceLevels', option.value)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Job Type Filter */}
          <FilterSection
            title="Job Type"
            isExpanded={expandedSections.jobType}
            onToggle={() => toggleSection('jobType')}
          >
            <div className="space-y-2">
              {[
                { value: 'hourly', label: 'Hourly' },
                { value: 'fixed', label: 'Fixed-Price' }
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.jobType.includes(option.value)}
                    onChange={() => toggleFilterValue('jobType', option.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Number of Proposals Filter */}
          <FilterSection
            title="Number of Proposals"
            isExpanded={expandedSections.proposalRange}
            onToggle={() => toggleSection('proposalRange')}
          >
            <div className="space-y-2">
              {[
                { value: '0-5', label: 'Less than 5' },
                { value: '5-10', label: '5 to 10' },
                { value: '10-15', label: '10 to 15' },
                { value: '15-20', label: '15 to 20' },
                { value: '20-50', label: '20 to 50' },
                { value: '50+', label: '50+' }
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.proposalRanges.includes(option.value)}
                    onChange={() => toggleFilterValue('proposalRanges', option.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Client Info Filter */}
          <FilterSection
            title="Client Info"
            isExpanded={expandedSections.clientInfo}
            onToggle={() => toggleSection('clientInfo')}
          >
            <div className="space-y-2">
              {[
                { value: 'paymentVerified', label: 'Payment verified' },
                { value: 'previousClients', label: 'My previous clients' }
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.clientInfo.includes(option.value)}
                    onChange={() => toggleFilterValue('clientInfo', option.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Client History Filter */}
          <FilterSection
            title="Client History"
            isExpanded={expandedSections.clientHistory}
            onToggle={() => toggleSection('clientHistory')}
          >
            <div className="space-y-2">
              {[
                { value: 'noHires', label: 'No hires' },
                { value: '1-9', label: '1 to 9 hires' },
                { value: '10+', label: '10+ hires' }
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.clientHistory.includes(option.value)}
                    onChange={() => toggleFilterValue('clientHistory', option.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Client Location Filter */}
          <FilterSection
            title="Client Location"
            isExpanded={expandedSections.clientLocation}
            onToggle={() => toggleSection('clientLocation')}
          >
            <select
              value={filters.clientLocation}
              onChange={(e) => setFilter('clientLocation', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            >
              <option value="">Any location</option>
              <option value="US">United States</option>
              <option value="UK">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="NL">Netherlands</option>
              <option value="CH">Switzerland</option>
              <option value="AE">United Arab Emirates</option>
              <option value="SG">Singapore</option>
            </select>
          </FilterSection>

          {/* Client Time Zones Filter */}
          <FilterSection
            title="Client Time Zones"
            isExpanded={expandedSections.clientTimeZones}
            onToggle={() => toggleSection('clientTimeZones')}
          >
            <div className="space-y-2">
              {[
                { value: 'americas', label: 'Americas (UTC-8 to UTC-3)' },
                { value: 'europe', label: 'Europe/Africa (UTC-1 to UTC+3)' },
                { value: 'asia', label: 'Asia/Pacific (UTC+5 to UTC+12)' }
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.clientTimeZones.includes(option.value)}
                    onChange={() => toggleFilterValue('clientTimeZones', option.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Project Length Filter */}
          <FilterSection
            title="Project Length"
            isExpanded={expandedSections.projectLength}
            onToggle={() => toggleSection('projectLength')}
          >
            <div className="space-y-2">
              {[
                { value: '<1month', label: 'Less than one month' },
                { value: '1-3months', label: '1 to 3 months' },
                { value: '3-6months', label: '3 to 6 months' },
                { value: '6+months', label: 'More than 6 months' }
              ].map((option) => (
                <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.projectLength.includes(option.value)}
                    onChange={() => toggleFilterValue('projectLength', option.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
            </div>
          </FilterSection>

          {/* Budget Range Filter */}
          <FilterSection
            title="Budget Range"
            isExpanded={expandedSections.budgetRange}
            onToggle={() => toggleSection('budgetRange')}
          >
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Min</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={filters.budgetMin}
                    onChange={(e) => setFilter('budgetMin', e.target.value)}
                    placeholder="0"
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
              <span className="text-gray-400 mt-5">-</span>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Max</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    type="number"
                    value={filters.budgetMax}
                    onChange={(e) => setFilter('budgetMax', e.target.value)}
                    placeholder="Any"
                    className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  />
                </div>
              </div>
            </div>
          </FilterSection>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={clearAllFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            Clear all
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition-colors"
          >
            Apply {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Filter Section Component
 */
const FilterSection = ({ title, isExpanded, onToggle, children }) => {
  return (
    <div className="border-b border-gray-200 py-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left"
      >
        <span className="text-sm font-medium text-gray-900">{title}</span>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isExpanded && (
        <div className="mt-3">
          {children}
        </div>
      )}
    </div>
  );
};

export default JobFiltersModal;
